/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { DescriptionPage_CourseStatus as DescriptionPageCourseStatus } from '__generated__/graphql-types';

import user from 'js/lib/user';

import { Grid, Link, PageGridContainer, Typography2, breakpoints } from '@coursera/cds-core';
import { EditIcon, EyeIcon } from '@coursera/cds-icons';

import CourseRoles from 'bundles/common/constants/CourseRoles';
import { useOpenCourseMemberships } from 'bundles/unified-description-page-common/contexts/UserContext';

import _t from 'i18n!nls/xdp';

type Props = {
  slug: string;
  courseId?: string;
  courseStatus?: DescriptionPageCourseStatus | null;
};

const NotEnrolledRoles = new Set<keyof typeof CourseRoles>([CourseRoles.NOT_ENROLLED, CourseRoles.BROWSER]);

const styles = {
  gridContainer: () => css`
    min-height: 60px;
    border-bottom: 2px solid var(--cds-color-grey-50);
    margin: var(--cds-spacing-200) 0;
  `,
  linksColumn: () => css`
    justify-content: flex-end;
    ${breakpoints.down('xs')} {
      justify-content: flex-start;
      margin-top: var(--cds-spacing-200);
    }
  `,
  previewContentLink: () => css`
    margin-left: var(--cds-spacing-300);
  `,
};

export const AdminBannerNotConfigurable = ({ courseId, courseStatus, slug }: Props) => {
  const openCourseMemberships = useOpenCourseMemberships();

  // openCourseMemberships data isn't fetched until client, this shouldn't render in server.
  if (!user.isAuthenticatedUser() || !openCourseMemberships) {
    return null;
  }

  const isEnrolled = !NotEnrolledRoles.has(openCourseMemberships.courseRole);
  const isBetaTester = courseStatus === DescriptionPageCourseStatus.PreEnroll && openCourseMemberships.isLearner;
  const isMentor = courseStatus === DescriptionPageCourseStatus.PreEnroll && openCourseMemberships.isMentor;
  const isEnrolledStaff = user.get().is_staff && isEnrolled;
  const isSuperuser = user.isSuperuser();

  if (!(isSuperuser || isBetaTester || isMentor || isEnrolledStaff)) {
    return null;
  }

  const getRole = (): string | undefined => {
    if (isSuperuser) {
      return _t('Superuser');
    } else if (isEnrolledStaff) {
      return _t('Staff');
    } else if (isBetaTester) {
      return _t('Beta Tester');
    } else if (isMentor) {
      return _t('Mentor');
    } else {
      return undefined;
    }
  };

  const getAdminLink = (): string | undefined => {
    if (!slug) {
      return undefined;
    }

    if (!courseId) {
      return `/teach-specialization/${slug}`;
    } else if (courseId) {
      return `/teach/${slug}`;
    }
    return undefined;
  };

  return (
    <PageGridContainer>
      <Grid item container xs={12} css={styles.gridContainer}>
        <Grid item sm={6} xs={12}>
          <Typography2 component="h3" variant="subtitleMedium">
            {_t('Viewing as #{userRole}', {
              userRole: getRole(),
            })}
          </Typography2>
        </Grid>
        <Grid item sm={6} xs={12} container css={styles.linksColumn}>
          {isSuperuser && (
            <Link
              href={getAdminLink()}
              variant="quiet"
              typographyVariant="bodyPrimary"
              icon={<EditIcon />}
              iconPosition="before"
            >
              {_t('Edit content')}
            </Link>
          )}
          {courseId && (isBetaTester || isMentor || isEnrolledStaff) && (
            <Link
              href={`/learn/${slug}/home/welcome`}
              variant="quiet"
              typographyVariant="bodyPrimary"
              icon={<EyeIcon />}
              iconPosition="before"
              css={styles.previewContentLink}
            >
              {_t(
                // eslint-disable-next-line no-restricted-syntax
                'Preview Course Materials'
              )}
            </Link>
          )}
        </Grid>
      </Grid>
    </PageGridContainer>
  );
};

export default AdminBannerNotConfigurable;
